import { PROCESSING_STATUS } from '@bower/shared/constants/artifact-status';
import type { ApiResponse, UploadStatusResponse } from '@bower/shared';
import { onSyncSignal } from '../../lib/syncSignals';
import type { HttpClient } from '../api/http-client.types';
import { getUploadStatus } from '../api/uploads.api';

/**
 * Wait for a finalized upload (a video transcode, a voice note's preparation)
 * to reach `ready` or `failed`, without polling.
 *
 * The status route is read once up front, then again only when something may
 * have moved the row: a `/sync` change naming this upload (the transcode's
 * READY and FAILED flips are conditional `updateMany`s, which the audit
 * extension's bulk hook broadcasts), or a `/sync` room join, which covers a
 * flip broadcast while the socket was down, such as a phone backgrounded across
 * the encode. The `upload:complete` notification is not used: it is a
 * fire-and-forget emit with no persistence, and the row's own state is what a
 * re-read trusts.
 *
 * A failed READ is retried on a short bounded ladder, since no event is coming
 * to prompt the next one; past the ladder it waits on events like any other
 * read.
 */

/** Retries for a status read that failed in transport, not a poll schedule. */
export const TRANSPORT_RETRY_DELAYS_MS = [1_000, 2_000, 5_000] as const;

/** Slightly over the transcode job's own 540s cap, so a wait that gives up means
 * the job really is gone rather than merely slow. */
export const TRANSCODE_TIMEOUT_MS = 600_000;

/**
 * Subscribe to "this upload may have changed"; returns the unsubscribe.
 * `left` is true when the client joined ANOTHER workspace's room: the change
 * still earns one re-read, but no event for this upload can arrive after it.
 */
export type SubscribeToUploadChanges = (
  upload: { readonly uploadId: string; readonly workspaceId: string },
  onChange: (left: boolean) => void,
) => () => void;

const subscribeToSyncSignals: SubscribeToUploadChanges = ({ uploadId, workspaceId }, onChange) =>
  onSyncSignal((signal) => {
    if (signal.kind === 'joined') onChange(signal.workspaceId !== workspaceId);
    else if (signal.entityId === uploadId) onChange(false);
  });

interface WaitForUploadReadyOptions {
  /** Defaults to `TRANSCODE_TIMEOUT_MS`. */
  readonly timeoutMs?: number;
  readonly transportRetryDelaysMs?: readonly number[];
  /** Defaults to the `/sync` signals `SocketContext` publishes. */
  readonly subscribe?: SubscribeToUploadChanges;
  readonly signal?: AbortSignal;
}

export type UploadReadinessErrorCode =
  | 'TIMEOUT'
  /** The client switched workspace; this upload's events no longer reach it. */
  | 'WORKSPACE_LEFT'
  | 'FAILED'
  | 'UPLOAD_NOT_FOUND'
  | 'UNEXPECTED_STATUS'
  | 'TRANSPORT_ERROR'
  | 'CANCELED';

const TERMINAL_READINESS_CODES = new Set<UploadReadinessErrorCode>([
  'FAILED',
  'UPLOAD_NOT_FOUND',
  'CANCELED',
]);

export class UploadReadinessError extends Error {
  readonly retryable: boolean;

  constructor(
    readonly code: UploadReadinessErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'UploadReadinessError';
    this.retryable = !TERMINAL_READINESS_CODES.has(code);
  }
}

function canceledError(): UploadReadinessError {
  return new UploadReadinessError('CANCELED', 'Audio preparation was canceled.');
}

function throwIfCanceled(signal?: AbortSignal): void {
  if (signal?.aborted) throw canceledError();
}

/**
 * Changes since the last read. Subscribed before the first read, so a flip that
 * broadcasts while a read is in flight is not lost.
 */
function changeLatch(
  upload: { readonly uploadId: string; readonly workspaceId: string },
  subscribe: SubscribeToUploadChanges,
) {
  let changed = false;
  let left = false;
  let wake: (() => void) | undefined;
  const unsubscribe = subscribe(upload, (leftWorkspace) => {
    changed = true;
    // A later join back to this workspace makes its events reachable again.
    left = leftWorkspace;
    wake?.();
  });
  /** Resolves on a change (or one already seen), or after `maxWaitMs`. */
  const next = (maxWaitMs: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      const settle = () => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        wake = undefined;
        changed = false;
      };
      const finish = () => {
        settle();
        resolve();
      };
      const onAbort = () => {
        settle();
        reject(canceledError());
      };
      const timer = setTimeout(finish, Math.max(0, maxWaitMs));
      wake = finish;
      signal?.addEventListener('abort', onAbort, { once: true });
      if (signal?.aborted) onAbort();
      else if (changed) finish();
    });
  return { next, close: unsubscribe, hasLeft: () => left };
}

async function getStatusBeforeDeadline(
  http: HttpClient,
  workspaceId: string,
  uploadId: string,
  options: { readonly deadline: number; readonly signal?: AbortSignal },
) {
  const remainingMs = Math.max(1, options.deadline - Date.now());
  const controller = new AbortController();
  const abortTimer = setTimeout(() => controller.abort(), remainingMs);
  const cancelRequest = () => controller.abort();
  options.signal?.addEventListener('abort', cancelRequest, { once: true });
  if (options.signal?.aborted) cancelRequest();

  try {
    return await getUploadStatus(http, workspaceId, uploadId, {
      signal: controller.signal,
      timeoutMs: remainingMs,
    });
  } finally {
    clearTimeout(abortTimer);
    options.signal?.removeEventListener('abort', cancelRequest);
  }
}

type ReadinessDecision = 'ready' | 'wait' | 'transport' | UploadReadinessError;

function classifyResponse(response: ApiResponse<UploadStatusResponse>): ReadinessDecision {
  if (!response.success) {
    if (response.error?.code === 'UPLOAD_NOT_FOUND') {
      return new UploadReadinessError(
        'UPLOAD_NOT_FOUND',
        'This upload is no longer available. Your recording is still saved.',
      );
    }
    if (response.error?.code === 'INVALID_STATUS') {
      return new UploadReadinessError(
        'UNEXPECTED_STATUS',
        'Audio preparation returned an unexpected status. Your recording is still saved.',
      );
    }
    return 'transport';
  }

  const status = response.data?.processingStatus;
  if (status === PROCESSING_STATUS.READY) return 'ready';
  if (status === PROCESSING_STATUS.FAILED) {
    return new UploadReadinessError(
      'FAILED',
      'Audio preparation failed. Your recording is still saved.',
    );
  }
  if (status === PROCESSING_STATUS.UPLOADING || status === PROCESSING_STATUS.TRANSCODING) {
    return 'wait';
  }
  return new UploadReadinessError(
    'UNEXPECTED_STATUS',
    'Audio preparation returned an unexpected status. Your recording is still saved.',
  );
}

export async function waitForUploadReady(
  http: HttpClient,
  workspaceId: string,
  uploadId: string,
  options: WaitForUploadReadyOptions = {},
): Promise<void> {
  const retryDelays = options.transportRetryDelaysMs ?? TRANSPORT_RETRY_DELAYS_MS;
  const deadline = Date.now() + (options.timeoutMs ?? TRANSCODE_TIMEOUT_MS);
  const changes = changeLatch(
    { uploadId, workspaceId },
    options.subscribe ?? subscribeToSyncSignals,
  );
  let transportFailures = 0;

  try {
    while (Date.now() < deadline) {
      throwIfCanceled(options.signal);
      const response = await getStatusBeforeDeadline(http, workspaceId, uploadId, {
        deadline,
        signal: options.signal,
      }).catch(() => undefined);
      throwIfCanceled(options.signal);
      const decision = response ? classifyResponse(response) : 'transport';
      if (decision === 'ready') return;
      if (decision instanceof UploadReadinessError) throw decision;
      const remainingMs = deadline - Date.now();
      if (remainingMs <= 0) break;
      if (decision === 'transport') {
        const retryMs = retryDelays[transportFailures];
        transportFailures += 1;
        // A change arriving first re-reads sooner; either way, read again.
        if (retryMs !== undefined) {
          await changes.next(Math.min(retryMs, remainingMs), options.signal);
          continue;
        }
      } else {
        transportFailures = 0;
      }
      // That read was the last one an event can prompt: waiting to the deadline
      // would leave the row "processing" for minutes after it finished.
      if (changes.hasLeft()) {
        throw new UploadReadinessError(
          'WORKSPACE_LEFT',
          'Stopped following this upload after a workspace switch. Your recording is still saved.',
        );
      }
      await changes.next(remainingMs, options.signal);
    }
  } finally {
    changes.close();
  }

  throw new UploadReadinessError(
    transportFailures > 0 ? 'TRANSPORT_ERROR' : 'TIMEOUT',
    transportFailures > 0
      ? 'Audio preparation could not be checked. Your recording is still saved.'
      : 'Audio preparation timed out. Your recording is still saved.',
  );
}
